/**
 * Copyright 2022-present terrestris GmbH & Co. KG
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *
 * 1. Redistributions of source code must retain the above copyright notice, this
 * list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright notice,
 * this list of conditions and the following disclaimer in the documentation
 * and/or other materials provided with the distribution.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 */
import React, {
  Key,
  useState,
  useEffect
} from 'react';

import {
  AntTreeNodeCheckedEvent
} from 'antd/lib/tree';

import _isNil from 'lodash/isNil';

import { getUid } from 'ol';
import OlBaseLayer from 'ol/layer/Base';
import OlLayerGroup from 'ol/layer/Group';
import OlLayer from 'ol/layer/Layer';
import OlSource from 'ol/source/Source';
import OlSourceVector from 'ol/source/Vector';

import {
  useTranslation
} from 'react-i18next';

import MapUtil from '@terrestris/ol-util/dist/MapUtil/MapUtil';

import {
  useMap
} from '@terrestris/react-geo/dist/Hook/useMap';
import RgLayerTree, {
  LayerTreeProps as RgLayerTreeProps
} from '@terrestris/react-geo/dist/LayerTree/LayerTree';
import Legend from '@terrestris/react-geo/dist/Legend/Legend';
import LayerTransparencySlider from '@terrestris/react-geo/dist/Slider/LayerTransparencySlider/LayerTransparencySlider';
import {
  WmsLayer
} from '@terrestris/react-geo/dist/Util/typeUtils';

import {
  useAppDispatch
} from '../../hooks/useAppDispatch';
import {
  useAppSelector
} from '../../hooks/useAppSelector';
import {
  setSelectedTopic,
  LayerState
} from '../../store/layer';

import LayerTreeContextMenu from './LayerTreeContextMenu/LayerTreeContextMenu';

import './LayerTree.less';

export type LayerTreeProps = {} & Partial<RgLayerTreeProps>;

export const LayerTree: React.FC<LayerTreeProps> = ({
  ...restProps
}): JSX.Element => {
  const map = useMap();
  const {
    t
  } = useTranslation();

  const dispatch = useAppDispatch();
  const selectedTopic = useAppSelector((state) => state.layer.selectedTopic);

  const [invisibleLegendsIds, setInvisibleLegendsIds] = useState<string[]>([]);
  const [selectedLayerKeys, setSelectedLayerKeys] = useState<Key[]>([]);
  const [expandedFolderKeys, setExpandedFolderKeys] = useState<Key[]>([]);

  /*
   * Generates initial list of selected layers and expanded folders.
   */
  useEffect(() => {
    const checkedKeys = getVisibleOlUids();
    const themenFolder = map ? map.getLayers().getArray().find(
      el => el.get('name') === 'Themen') : null;
    const expandedKeys = [];
    if (themenFolder && themenFolder instanceof OlLayerGroup) {
      expandedKeys.push(getUid(themenFolder));
    }
    setSelectedLayerKeys(checkedKeys);
    setExpandedFolderKeys(expandedKeys);
  }, [map]);

  const getVisibleOlUids = (): string[] => {
    if (!map) {
      return [];
    }

    return MapUtil.getAllLayers(map,
      (layer: OlBaseLayer) => {
        return !(layer instanceof OlLayerGroup) && layer.getVisible();
      })
      .map(getUid);
  };

  const onLayerSelection = (checkedKeys: Key[], e: AntTreeNodeCheckedEvent) => {
    if (!map) {
      return;
    }
    const { checked = false } = e;
    const eventKey = e.node.props.eventKey;
    const filteredCheckedKeys: Key[] = [];
    if (!_isNil(eventKey)) {
      const layer = MapUtil.getLayerByOlUid(map, eventKey);
      if (!layer) {
        return;
      }
      const layerName = layer.get('name');
      const pvLayer = MapUtil.getLayerByName(map, 'Photovoltaik'); // todo: make configurable
      const thLayer = MapUtil.getLayerByName(map, 'Solarthermie');
      const gruenLayer = MapUtil.getLayerByName(map, 'Gründach');
      switch (layerName) {
        case 'Photovoltaik':
          setLayerVisibility(thLayer, false);
          setLayerVisibility(gruenLayer, false);
          filteredCheckedKeys.push(getUid(thLayer));
          filteredCheckedKeys.push(getUid(gruenLayer));
          break;
        case 'Solarthermie':
          setLayerVisibility(gruenLayer, false);
          setLayerVisibility(pvLayer, false);
          filteredCheckedKeys.push(getUid(gruenLayer));
          filteredCheckedKeys.push(getUid(pvLayer));
          break;
        case 'Gründach':
          setLayerVisibility(thLayer, false);
          setLayerVisibility(pvLayer, false);
          filteredCheckedKeys.push(getUid(thLayer));
          filteredCheckedKeys.push(getUid(pvLayer));
          break;
        default:
          break;
      }
      setLayerVisibility(layer, checked);
      dispatch(setSelectedTopic({ selectedTopic: layerName }));
    }
    setSelectedLayerKeys(checkedKeys.filter(n => !filteredCheckedKeys.includes(n)));
  };

  const onFolderExpand = (expandedKeys: Key[], e: AntTreeNodeCheckedEvent) => {
    setExpandedFolderKeys(expandedKeys);
  };

  const setLayerVisibility = (layer: OlBaseLayer, visibility: boolean) => {
    if (layer instanceof OlLayerGroup) {
      layer.setVisible(visibility);
      layer.getLayers().forEach((subLayer) => {
        setLayerVisibility(subLayer, visibility);
      });
    } else {
      layer.setVisible(visibility);
    }
  };

  const treeFilterFunction = (layer: OlLayer<OlSource> | OlLayerGroup) => {
    if (layer instanceof OlLayerGroup) {
      return !layer.get('hideInLayerTree');
    } else {
      return !layer.get('isBackgroundLayer') &&
        !(layer.getSource && layer.getSource() instanceof OlSourceVector);
    }
  };

  const treeNodeTitleRenderer = (layer: OlBaseLayer) => {
    if (!map) {
      return;
    }

    const mapView = map.getView();
    const unit = mapView.getProjection().getUnits() || 'm';
    const resolution = mapView.getResolution();
    const scale = resolution ? MapUtil.getScaleForResolution(resolution, unit) : undefined;

    if (layer instanceof OlLayerGroup) {
      return (
        <div>
          {layer.get('name')}
        </div>
      );
    } else {
      return (
        <>
          <div className="tree-node-header">
            <span>{layer.get('name')}</span>
            <LayerTreeContextMenu
              layer={(layer as WmsLayer)}
              invisibleLegendsIds={invisibleLegendsIds}
              setInvisibleLegendsIds={setInvisibleLegendsIds}
            />
          </div>
          {
            layer.get('visible') &&
              <>
                <div className="layer-transparency">
                  <LayerTransparencySlider
                    tipFormatter={val => `${t('LayerTree.transparency')}: ${val}%`}
                    layer={layer}
                  />
                </div>
              </>
          }
          {
            layer.get('visible') && !invisibleLegendsIds.includes(getUid(layer)) &&
              <Legend
                layer={layer as WmsLayer}
                errorMsg={t('LayerTree.noLegendAvailable')}
                extraParams={{
                  scale,
                  LEGEND_OPTIONS: 'fontAntiAliasing:true;forceLabels:on',
                  TRANSPARENT: true
                }}
              />
          }
        </>
      );
    }
  };

  if (!map) {
    return <></>;
  }
  return (
    <RgLayerTree
      className="layertree"
      map={map}
      nodeTitleRenderer={treeNodeTitleRenderer}
      filterFunction={treeFilterFunction}
      checkedKeys={selectedLayerKeys}
      onCheck={onLayerSelection as any}
      onExpand={onFolderExpand as any}
      expandedKeys={expandedFolderKeys}
      draggable={{
        icon: false
      }}
      {...restProps}
    />
  );
};

export default LayerTree;
